/**
 * Eén interactiemodel voor de Builder.
 *
 * Selection IDs blijven onderdeel van de editor-documentstate. Deze state
 * beschrijft uitsluitend wat de pointer/keyboard-interactie op dit moment
 * doet. Daardoor kunnen Canvas, element-controls en Inspector niet ieder hun
 * eigen combinatie van booleans interpreteren.
 */
import type { ViewportKey } from './types';

/**
 * Hard product rule: the visual grid may guide or snap a placement, but it
 * never reserves exclusive space. Sibling rectangles are never barriers.
 */
export const ELEMENT_COLLISION_BLOCKING = false;

export type InteractionPhase =
  | 'IDLE'
  | 'HOVERING_ELEMENT'
  | 'ELEMENT_SELECTED'
  | 'TEXT_EDITING'
  | 'DRAGGING_ELEMENT'
  | 'RESIZING_ELEMENT'
  | 'SECTION_SELECTED'
  | 'INSPECTOR_EDITING';

export type ResizeAxis = 'width' | 'height' | 'corner' | 'width-left';

export interface EditorInteractionState {
  phase: InteractionPhase;
  targetId: string | null;
  sectionId: string | null;
  viewport: ViewportKey | null;
  resizeAxis: ResizeAxis | null;
}

export type EditorInteractionAction =
  | { type: 'hoverElement'; targetId: string }
  | { type: 'selectElement'; targetId: string }
  | { type: 'editText'; targetId: string }
  | { type: 'startDrag'; targetId: string; viewport: ViewportKey }
  | { type: 'startResize'; targetId: string; viewport: ViewportKey; axis: ResizeAxis }
  | { type: 'selectSection'; sectionId: string }
  | { type: 'openInspector'; targetId: string }
  | { type: 'finishManipulation' }
  | { type: 'finishTextEdit' }
  | { type: 'clear' };

export const initialEditorInteraction: EditorInteractionState = {
  phase: 'IDLE',
  targetId: null,
  sectionId: null,
  viewport: null,
  resizeAxis: null,
};

export function editorInteractionReducer(
  state: EditorInteractionState,
  action: EditorInteractionAction,
): EditorInteractionState {
  switch (action.type) {
    case 'hoverElement':
      if (state.phase === 'DRAGGING_ELEMENT' || state.phase === 'RESIZING_ELEMENT') return state;
      return { ...state, phase: 'HOVERING_ELEMENT', targetId: action.targetId, sectionId: null, resizeAxis: null };
    case 'selectElement':
      return { phase: 'ELEMENT_SELECTED', targetId: action.targetId, sectionId: null, viewport: null, resizeAxis: null };
    case 'editText':
      return { phase: 'TEXT_EDITING', targetId: action.targetId, sectionId: null, viewport: null, resizeAxis: null };
    case 'startDrag':
      return { phase: 'DRAGGING_ELEMENT', targetId: action.targetId, sectionId: null, viewport: action.viewport, resizeAxis: null };
    case 'startResize':
      return { phase: 'RESIZING_ELEMENT', targetId: action.targetId, sectionId: null, viewport: action.viewport, resizeAxis: action.axis };
    case 'selectSection':
      return { phase: 'SECTION_SELECTED', targetId: null, sectionId: action.sectionId, viewport: null, resizeAxis: null };
    case 'openInspector':
      return { phase: 'INSPECTOR_EDITING', targetId: action.targetId, sectionId: null, viewport: null, resizeAxis: null };
    case 'finishManipulation':
      return state.targetId ? { ...state, phase: 'ELEMENT_SELECTED', viewport: null, resizeAxis: null } : initialEditorInteraction;
    case 'finishTextEdit':
      return state.targetId ? { ...state, phase: 'ELEMENT_SELECTED' } : initialEditorInteraction;
    case 'clear':
      return initialEditorInteraction;
    default:
      return state;
  }
}
